export interface FieldDef {
  key: string;
  label: string;
  type: 'number' | 'text' | 'select' | 'textarea';
  unit?: string;
  options?: readonly string[];
  info?: string;
  multiline?: boolean;
  gridSpan?: number;
}

// TASK-017C added the least-resistance option; wording per Vahid 2026-08-05
// ("Sheared along THE path of least resistance").
export const directionOfShearOptions = [
  'Sheared along long axis',
  'Sheared along short axis',
  'Sheared along step',
  'Sheared along slickensides',
  'Sheared along lineations',
  'Sheared along the path of least resistance',
] as const;

// TASK-017B: 'Constant Normal Stiffness' added for future-proofing.
export const testTypeOptions = [
  'Constant Normal Load',
  'Constant Normal Stress',
  'Constant Normal Stiffness',
] as const;

// BUG-2026-0201: the standard/common displacement-rate statement, kept as a
// single source of truth so the DS form pre-fills a REAL persisted value. It
// used to live only as a controlled-value fallback in CommonParametersForm,
// which displayed the text but never wrote it to state — so a test the tech
// left untouched saved NULL and the statement never reached the report.
export const DISPLACEMENT_RATE_DEFAULT =
  '0.5 mm/min for the first 2 mm, then 1 mm/min for the remaining 8 mm';

// TASK-017D: common displacement-rate presets exposed for the rate field's
// dropdown affordance. Free-form entry still allowed.
export const displacementRatePresets = [
  '0.05 mm/min for first 2 mm, then 0.1 mm/min for remaining 8 mm',
  '0.05 mm/min throughout',
  '0.1 mm/min throughout',
  '0.5 mm/min throughout',
] as const;

// TASK-017A: Dip + Dip Direction removed from the form (we never report them).
// TASK-017D: 'Displacement Description' renamed to 'Displacement Rate'.
// TASK-017F: 'Sheared Along' free-text field added.
export const commonFields: FieldDef[] = [
  { key: 'ellipseLengthA', label: 'Ellipse Length A', type: 'number', unit: 'mm' },
  { key: 'ellipseLengthB', label: 'Ellipse Length B', type: 'number', unit: 'mm' },
  { key: 'jrc', label: 'JRC', type: 'number', info: 'jrc' },
  { key: 'jointAngle', label: 'Joint Angle', type: 'number', unit: '°' },
  { key: 'leebHardnessFreshSurfaceTop', label: 'Leeb Hardness — Fresh Surface Top', type: 'number' },
  { key: 'leebHardnessFreshSurfaceBottom', label: 'Leeb Hardness — Fresh Surface Bottom', type: 'number' },
  { key: 'leebHardnessJointSurfaceTop', label: 'Leeb Hardness — Joint Surface Top', type: 'number' },
  { key: 'leebHardnessJointSurfaceBottom', label: 'Leeb Hardness — Joint Surface Bottom', type: 'number' },
  { key: 'displacementRateDescription', label: 'Displacement Rate', type: 'text', unit: 'mm/min',
    info: `Common: ${DISPLACEMENT_RATE_DEFAULT}` },
  { key: 'testType', label: 'Test Type', type: 'select', options: testTypeOptions },
  { key: 'directionOfShear', label: 'Direction of Shear', type: 'select', options: directionOfShearOptions },
  { key: 'shearedAlong', label: 'Sheared Along', type: 'text' },
  { key: 'remarks', label: 'Remarks', type: 'textarea', gridSpan: 2 },
];

export const stageFields: { key: string; label: string; unit: string }[] = [
  { key: 'area', label: 'Area', unit: 'm²' },
  { key: 'initialStress', label: 'Stress', unit: 'kPa' },
  { key: 'initialLoad', label: 'Load', unit: 'kN' },
  // Vertical Stress Offset removed per user feedback — not needed
];
